/**
 * Reorder lead-time resolution (see TECHNICAL_DOCUMENTATION.txt — Reorder intelligence).
 * Order: (1) median historical PO order_date → delivery for SKU, (2) supplier.default_lead_time_days when SKU is on a PO line, (3) unknown.
 */

export type LeadTimeSource = "historical_median" | "supplier_default" | "unknown";

function medianSorted(sorted: number[]): number | null {
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Calendar days between order_date and delivery end (delivery_date, or received_at date). */
export function daysBetweenOrderAndDelivery(orderDateStr: string, deliveryDateStr: string, receivedAtIso: string | null): number {
  const o = new Date(orderDateStr + "T12:00:00Z").getTime();
  let endMs: number;
  if (receivedAtIso) {
    endMs = new Date(receivedAtIso).getTime();
  } else {
    endMs = new Date(deliveryDateStr + "T12:00:00Z").getTime();
  }
  const d = Math.round((endMs - o) / (24 * 60 * 60 * 1000));
  return Math.max(0, d);
}

export function medianLeadDaysFromSamples(samples: number[], maxSamples = 8): number | null {
  const usable = samples.filter((n) => n > 0 && n <= 120).slice(-maxSamples);
  if (usable.length === 0) return null;
  return medianSorted([...usable].sort((a, b) => a - b));
}

/** True when the business has suppliers or at least one delivered Transport & Logistics record. */
export function businessHasLogisticsData(
  suppliers: { id: string }[],
  deliveries: { status: string; received_at: string | null }[]
): boolean {
  if (suppliers.length > 0) return true;
  return deliveries.some((d) => d.status === "delivered" || d.received_at != null);
}

export function resolveLeadTimeDays(input: {
  historicalSamples: number[];
  supplierDefaultDays: number | null | undefined;
  hasPoLink: boolean;
}): { days: number | null; source: LeadTimeSource } {
  const hist = medianLeadDaysFromSamples(input.historicalSamples);
  if (hist != null) return { days: Math.round(hist), source: "historical_median" };
  if (input.hasPoLink && input.supplierDefaultDays != null && input.supplierDefaultDays > 0) {
    return { days: input.supplierDefaultDays, source: "supplier_default" };
  }
  return { days: null, source: "unknown" };
}

export const REORDER_SETUP_CTA =
  "We can't estimate delivery time yet — add a supplier with usual delivery days and include this product on a purchase order in Transport & Logistics.";
